import { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDebug } from '../context/DebugContext';
import { useLattices, useCircuits } from '../hooks/useHttpPolling';
import { 
  startLattice, 
  stopLattice, 
  configureLattice,
  getLatticeLogsUrl,
  LatticeConfig,
  LatticeActionResult,
} from '../services/LatticeService';
import './Lattices.css';

interface Notification {
  id: string;
  type: 'success' | 'error' | 'info';
  message: string;
}

interface ConfirmDialog {
  action: 'start' | 'stop' | 'configure';
  latticeId: string;
  latticeName: string;
}

interface ConfigModal {
  latticeId: string;
  latticeName: string;
  currentConfig: Record<string, unknown>;
}

interface Circuit {
  id: string;
  name: string;
  version: string;
  status: string;
  latticeId?: string;
  deployedAt?: string;
}

type TabId = 'overview' | 'nodes' | 'circuits';

export default function Lattices() {
  const { addEvent } = useDebug();
  const location = useLocation();
  const navigate = useNavigate();
  const { data: latticesData, loading, refresh } = useLattices();
  const { data: circuitsData } = useCircuits();
  
  const [actionLoading, setActionLoading] = useState<Record<string, boolean>>({});
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialog | null>(null);
  const [configModal, setConfigModal] = useState<ConfigModal | null>(null);
  const [selectedLattice, setSelectedLattice] = useState<LatticeConfig | null>(null);
  
  // Determine active tab from URL
  const getActiveTab = (): TabId => {
    if (location.pathname.includes('/nodes')) return 'nodes';
    if (location.pathname.includes('/circuits')) return 'circuits';
    return 'overview';
  };
  const [activeTab, setActiveTab] = useState<TabId>(getActiveTab());
  
  // Map API response to LatticeConfig format
  const lattices: LatticeConfig[] = (latticesData?.lattices || []).map((l: any) => ({
    id: l.id,
    name: l.name,
    network: l.network || 'devnet',
    status: l.status,
    nodeCount: l.nodeCount || 1,
    eventsPerSecond: l.eventsPerSecond || 0,
    region: l.region,
    createdAt: l.createdAt,
  }));
  
  // Map circuits and group by lattice
  const circuits: Circuit[] = (circuitsData?.deployed || []).map((c: any) => ({
    id: c.id,
    name: c.name,
    version: c.version || '1.0.0',
    status: c.status || 'deployed',
    latticeId: c.latticeId || c.lattice_id,
    deployedAt: c.deployedAt || c.deployed_at,
  }));
  
  // Get circuits for a specific lattice
  const getCircuitsForLattice = (latticeId: string) => {
    return circuits.filter(c => c.latticeId === latticeId);
  };

  useEffect(() => {
    addEvent({
      source: 'console',
      category: 'navigation',
      message: 'Viewed Lattices page',
    });
  }, [addEvent]);
  
  // Sync tab with URL
  useEffect(() => {
    setActiveTab(getActiveTab());
  }, [location.pathname]);
  
  const handleTabChange = (tab: TabId) => {
    setActiveTab(tab);
    if (tab === 'overview') navigate('/lattices');
    else if (tab === 'nodes') navigate('/lattices/nodes');
    else if (tab === 'circuits') navigate('/lattices/circuits');
  };

  // Show notification
  const showNotification = useCallback((type: 'success' | 'error' | 'info', message: string) => {
    const id = `${Date.now()}-${Math.random()}`;
    setNotifications(prev => [...prev, { id, type, message }]);
    
    // Auto-dismiss after 5 seconds
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, 5000);
  }, []);

  // Handle start lattice
  const handleStart = async (latticeId: string, latticeName: string) => {
    setActionLoading(prev => ({ ...prev, [latticeId]: true }));
    
    addEvent({
      source: 'console',
      category: 'api',
      message: `Starting lattice: ${latticeName}`,
      data: { latticeId },
    });
    
    const result: LatticeActionResult = await startLattice(latticeId);
    
    setActionLoading(prev => ({ ...prev, [latticeId]: false }));
    
    if (result.success) {
      showNotification('success', `Lattice "${latticeName}" is starting...`);
      addEvent({
        source: 'console',
        category: 'api',
        message: `Lattice started: ${latticeName}`,
        data: { latticeId, result },
      });
      // Refresh lattice list
      refresh();
    } else {
      showNotification('error', result.error || 'Failed to start lattice');
      addEvent({
        source: 'console',
        category: 'error',
        message: `Failed to start lattice: ${result.error}`,
        data: { latticeId, result },
      });
    }
    
    setConfirmDialog(null);
  };

  // Handle stop lattice
  const handleStop = async (latticeId: string, latticeName: string) => {
    setActionLoading(prev => ({ ...prev, [latticeId]: true }));
    
    addEvent({
      source: 'console',
      category: 'api',
      message: `Stopping lattice: ${latticeName}`,
      data: { latticeId },
    });
    
    const result: LatticeActionResult = await stopLattice(latticeId);
    
    setActionLoading(prev => ({ ...prev, [latticeId]: false }));
    
    if (result.success) {
      showNotification('success', `Lattice "${latticeName}" is stopping...`);
      addEvent({
        source: 'console',
        category: 'api',
        message: `Lattice stopped: ${latticeName}`,
        data: { latticeId, result },
      });
      refresh();
    } else {
      showNotification('error', result.error || 'Failed to stop lattice');
      addEvent({
        source: 'console',
        category: 'error',
        message: `Failed to stop lattice: ${result.error}`,
        data: { latticeId, result },
      });
    }
    
    setConfirmDialog(null);
  };

  // Handle configure lattice
  const handleConfigure = (lattice: LatticeConfig) => {
    setConfigModal({
      latticeId: lattice.id,
      latticeName: lattice.name,
      currentConfig: {
        nodeCount: lattice.nodeCount,
        region: lattice.region,
        network: lattice.network,
      },
    });
  };

  // Save configuration
  const handleSaveConfig = async (config: Record<string, unknown>) => {
    if (!configModal) return;
    
    setActionLoading(prev => ({ ...prev, [configModal.latticeId]: true }));
    
    const result = await configureLattice(configModal.latticeId, config);
    
    setActionLoading(prev => ({ ...prev, [configModal.latticeId]: false }));
    
    if (result.success) {
      showNotification('success', `Configuration saved for "${configModal.latticeName}"`);
      addEvent({
        source: 'console',
        category: 'api',
        message: `Lattice configured: ${configModal.latticeName}`,
        data: { latticeId: configModal.latticeId, config },
      });
      refresh();
    } else {
      showNotification('error', result.error || 'Failed to save configuration');
    }
    
    setConfigModal(null);
  };

  // View logs
  const handleViewLogs = (latticeId: string) => {
    const url = getLatticeLogsUrl(latticeId);
    window.open(url, '_blank');
    addEvent({
      source: 'console',
      category: 'navigation',
      message: `Opened logs for lattice: ${latticeId}`,
      data: { latticeId, url },
    });
  };

  return (
    <div className="lattices-page">
      {/* Notifications */}
      <div className="notifications">
        {notifications.map(n => (
          <div key={n.id} className={`notification notification-${n.type}`}>
            <span>{n.message}</span>
            <button onClick={() => setNotifications(prev => prev.filter(x => x.id !== n.id))}>×</button>
          </div>
        ))}
      </div>
      
      {/* Confirm Dialog */}
      {confirmDialog && (
        <div className="modal-overlay" onClick={() => setConfirmDialog(null)}>
          <div className="confirm-dialog" onClick={e => e.stopPropagation()}>
            <h3>Confirm {confirmDialog.action}</h3>
            <p>
              Are you sure you want to {confirmDialog.action} lattice "{confirmDialog.latticeName}"?
            </p>
            <div className="dialog-actions">
              <button 
                className="btn-secondary"
                onClick={() => setConfirmDialog(null)}
              >
                Cancel
              </button>
              <button 
                className={`btn-${confirmDialog.action === 'stop' ? 'danger' : 'primary'}`}
                onClick={() => {
                  if (confirmDialog.action === 'start') {
                    handleStart(confirmDialog.latticeId, confirmDialog.latticeName);
                  } else if (confirmDialog.action === 'stop') {
                    handleStop(confirmDialog.latticeId, confirmDialog.latticeName);
                  }
                }}
                disabled={actionLoading[confirmDialog.latticeId]}
              >
                {actionLoading[confirmDialog.latticeId] ? 'Processing...' : confirmDialog.action}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Config Modal */}
      {configModal && (
        <ConfigureModal
          latticeName={configModal.latticeName}
          currentConfig={configModal.currentConfig}
          onSave={handleSaveConfig}
          onClose={() => setConfigModal(null)}
          loading={actionLoading[configModal.latticeId]}
        />
      )}
      
      {/* Lattice Detail Modal */}
      {selectedLattice && (
        <LatticeDetailModal
          lattice={selectedLattice}
          circuits={getCircuitsForLattice(selectedLattice.id)}
          onClose={() => setSelectedLattice(null)}
          onStart={() => setConfirmDialog({
            action: 'start',
            latticeId: selectedLattice.id,
            latticeName: selectedLattice.name,
          })}
          onStop={() => setConfirmDialog({
            action: 'stop',
            latticeId: selectedLattice.id,
            latticeName: selectedLattice.name,
          })}
          onConfigure={() => handleConfigure(selectedLattice)}
          onViewLogs={() => handleViewLogs(selectedLattice.id)}
          actionLoading={actionLoading[selectedLattice.id]}
        />
      )}
      
      <div className="page-header">
        <div className="header-left">
          <h2>Platform Lattices</h2>
          <p className="header-subtitle">Manage distributed compute lattices and their circuits</p>
        </div>
        <button className="btn-primary">+ Create Lattice</button>
      </div>
      
      {/* Tab Navigation */}
      <div className="tab-nav">
        <button 
          className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => handleTabChange('overview')}
        >
          Overview
        </button>
        <button 
          className={`tab-btn ${activeTab === 'nodes' ? 'active' : ''}`}
          onClick={() => handleTabChange('nodes')}
        >
          Nodes
        </button>
        <button 
          className={`tab-btn ${activeTab === 'circuits' ? 'active' : ''}`}
          onClick={() => handleTabChange('circuits')}
        >
          Circuits
        </button>
      </div>

      {loading ? (
        <div className="loading-container">
          <div className="spinner" />
          <span>Loading lattices...</span>
        </div>
      ) : (
        <>
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="lattice-grid">
              {lattices.length === 0 ? (
                <div className="empty-state">
                  <p>No lattices found. Create your first lattice to get started.</p>
                </div>
              ) : (
                lattices.map((lattice) => {
                  const latticeCircuits = getCircuitsForLattice(lattice.id);
                  return (
                    <div 
                      key={lattice.id} 
                      className={`lattice-card status-${lattice.status}`}
                      onClick={() => setSelectedLattice(lattice)}
                    >
                      <div className="lattice-header">
                        <span className="lattice-name">{lattice.name}</span>
                        <span className={`status-badge ${lattice.status}`}>
                          {lattice.status}
                        </span>
                      </div>

                      <div className="lattice-meta">
                        <span className="network-tag">{lattice.network}</span>
                        <span className="lattice-id">{lattice.id}</span>
                      </div>

                      <div className="lattice-stats">
                        <div className="stat">
                          <span className="stat-value">{lattice.nodeCount}</span>
                          <span className="stat-label">Nodes</span>
                        </div>
                        <div className="stat">
                          <span className="stat-value">{latticeCircuits.length}</span>
                          <span className="stat-label">Circuits</span>
                        </div>
                        <div className="stat">
                          <span className="stat-value">
                            {(lattice.eventsPerSecond || 0).toLocaleString()}
                          </span>
                          <span className="stat-label">Events/s</span>
                        </div>
                      </div>

                      {latticeCircuits.length > 0 && (
                        <div className="circuit-preview">
                          {latticeCircuits.slice(0, 3).map(c => (
                            <span key={c.id} className="circuit-chip">{c.name}</span>
                          ))}
                          {latticeCircuits.length > 3 && (
                            <span className="circuit-chip more">+{latticeCircuits.length - 3}</span>
                          )}
                        </div>
                      )}

                      <div className="lattice-actions" onClick={e => e.stopPropagation()}>
                        {lattice.status === 'stopped' ? (
                          <button
                            className="btn-action btn-start"
                            onClick={() => setConfirmDialog({
                              action: 'start',
                              latticeId: lattice.id,
                              latticeName: lattice.name,
                            })}
                            disabled={actionLoading[lattice.id]}
                          >
                            {actionLoading[lattice.id] ? '...' : 'Start'}
                          </button>
                        ) : (
                          <button
                            className="btn-action btn-stop"
                            onClick={() => setConfirmDialog({
                              action: 'stop',
                              latticeId: lattice.id,
                              latticeName: lattice.name,
                            })}
                            disabled={actionLoading[lattice.id]}
                          >
                            {actionLoading[lattice.id] ? '...' : 'Stop'}
                          </button>
                        )}
                        <button 
                          className="btn-action btn-config"
                          onClick={() => handleConfigure(lattice)}
                        >
                          Configure
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}
          
          {/* Nodes Tab */}
          {activeTab === 'nodes' && (
            <div className="nodes-view">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Node ID</th>
                    <th>Lattice</th>
                    <th>Region</th>
                    <th>Status</th>
                    <th>CPU</th>
                    <th>Memory</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {lattices.flatMap(lattice => 
                    Array.from({ length: lattice.nodeCount }, (_, i) => ({
                      id: `${lattice.id}-node-${i + 1}`,
                      latticeId: lattice.id,
                      latticeName: lattice.name,
                      region: lattice.region || 'us-central1',
                      status: lattice.status,
                      cpu: Math.floor(Math.random() * 40 + 10),
                      memory: Math.floor(Math.random() * 60 + 20),
                    }))
                  ).map(node => (
                    <tr key={node.id}>
                      <td className="mono">{node.id}</td>
                      <td>{node.latticeName}</td>
                      <td>{node.region}</td>
                      <td>
                        <span className={`status-badge ${node.status}`}>{node.status}</span>
                      </td>
                      <td>{node.cpu}%</td>
                      <td>{node.memory}%</td>
                      <td>
                        <button className="btn-sm">Logs</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          
          {/* Circuits Tab */}
          {activeTab === 'circuits' && (
            <div className="circuits-view">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Circuit</th>
                    <th>Version</th>
                    <th>Lattice</th>
                    <th>Status</th>
                    <th>Deployed</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {circuits.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="empty-row">
                        No circuits deployed. Deploy your first circuit from the Circuits page.
                      </td>
                    </tr>
                  ) : (
                    circuits.map(circuit => {
                      const lattice = lattices.find(l => l.id === circuit.latticeId);
                      return (
                        <tr key={circuit.id}>
                          <td>
                            <div className="circuit-name-cell">
                              <span className="circuit-name">{circuit.name}</span>
                              <span className="circuit-id mono">{circuit.id}</span>
                            </div>
                          </td>
                          <td className="mono">{circuit.version}</td>
                          <td>{lattice?.name || circuit.latticeId || 'Unassigned'}</td>
                          <td>
                            <span className={`status-badge ${circuit.status}`}>{circuit.status}</span>
                          </td>
                          <td>{circuit.deployedAt ? new Date(circuit.deployedAt).toLocaleDateString() : '-'}</td>
                          <td>
                            <button className="btn-sm">View</button>
                            <button className="btn-sm btn-danger">Undeploy</button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// Configure Modal Component
interface ConfigureModalProps {
  latticeName: string;
  currentConfig: Record<string, unknown>;
  onSave: (config: Record<string, unknown>) => void;
  onClose: () => void;
  loading?: boolean;
}

function ConfigureModal({ latticeName, currentConfig, onSave, onClose, loading }: ConfigureModalProps) {
  const [config, setConfig] = useState(currentConfig);
  
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="config-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Configure {latticeName}</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        
        <div className="config-form">
          <div className="form-group">
            <label>Network</label>
            <select 
              value={String(config.network || 'devnet')}
              onChange={e => setConfig(prev => ({ ...prev, network: e.target.value }))}
            >
              <option value="devnet">Devnet</option>
              <option value="testnet">Testnet</option>
              <option value="mainnet">Mainnet</option>
            </select>
          </div>
          
          <div className="form-group">
            <label>Region</label>
            <select
              value={String(config.region || 'us-central1')}
              onChange={e => setConfig(prev => ({ ...prev, region: e.target.value }))}
            >
              <option value="us-central1">US Central</option>
              <option value="us-west1">US West</option>
              <option value="us-east1">US East</option>
              <option value="europe-west1">Europe West</option>
              <option value="asia-east1">Asia East</option>
            </select>
          </div>
          
          <div className="form-group">
            <label>Target Node Count</label>
            <input
              type="number"
              min="1"
              max="100"
              value={Number(config.nodeCount) || 1}
              onChange={e => setConfig(prev => ({ ...prev, nodeCount: parseInt(e.target.value) }))}
            />
          </div>
        </div>
        
        <div className="modal-actions">
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <button 
            className="btn-primary" 
            onClick={() => onSave(config)}
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save Configuration'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Lattice Detail Modal Component
interface LatticeDetailModalProps {
  lattice: LatticeConfig;
  circuits: Circuit[];
  onClose: () => void;
  onStart: () => void;
  onStop: () => void;
  onConfigure: () => void;
  onViewLogs: () => void;
  actionLoading?: boolean;
}

function LatticeDetailModal({ 
  lattice, 
  circuits, 
  onClose, 
  onStart, 
  onStop, 
  onConfigure, 
  onViewLogs,
  actionLoading,
}: LatticeDetailModalProps) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="detail-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="header-info">
            <h3>{lattice.name}</h3>
            <span className={`status-badge ${lattice.status}`}>{lattice.status}</span>
          </div>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        
        <div className="detail-content">
          <div className="detail-section">
            <h4>Overview</h4>
            <div className="detail-grid">
              <div className="detail-item">
                <span className="detail-label">Lattice ID</span>
                <span className="detail-value mono">{lattice.id}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Network</span>
                <span className="detail-value">{lattice.network}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Region</span>
                <span className="detail-value">{lattice.region || 'us-central1'}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Nodes</span>
                <span className="detail-value">{lattice.nodeCount}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Events/s</span>
                <span className="detail-value">{(lattice.eventsPerSecond || 0).toLocaleString()}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Created</span>
                <span className="detail-value">
                  {lattice.createdAt ? new Date(lattice.createdAt).toLocaleDateString() : '-'}
                </span>
              </div>
            </div>
          </div>
          
          <div className="detail-section">
            <h4>Deployed Circuits ({circuits.length})</h4>
            {circuits.length === 0 ? (
              <p className="empty-text">No circuits deployed to this lattice.</p>
            ) : (
              <div className="circuits-list">
                {circuits.map(circuit => (
                  <div key={circuit.id} className="circuit-item">
                    <div className="circuit-info">
                      <span className="circuit-name">{circuit.name}</span>
                      <span className="circuit-version mono">v{circuit.version}</span>
                    </div>
                    <span className={`status-badge sm ${circuit.status}`}>{circuit.status}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        
        <div className="modal-actions">
          {lattice.status === 'stopped' ? (
            <button 
              className="btn-primary" 
              onClick={onStart}
              disabled={actionLoading}
            >
              {actionLoading ? 'Starting...' : 'Start Lattice'}
            </button>
          ) : (
            <button 
              className="btn-danger" 
              onClick={onStop}
              disabled={actionLoading}
            >
              {actionLoading ? 'Stopping...' : 'Stop Lattice'}
            </button>
          )}
          <button className="btn-secondary" onClick={onConfigure}>Configure</button>
          <button className="btn-secondary" onClick={onViewLogs}>View Logs</button>
        </div>
      </div>
    </div>
  );
}
